import React, { createContext, useContext } from 'react';

export interface ClientBrandColors {
  accent: string;
  accentHover: string;
  accentLight: string;
  avatarBackground: string;
  avatarText: string;
  /** Surface behind product images (rec cards + product view). Overrides --color-product-surface. Defaults to the cream design token. */
  imageSurface?: string;
  /** Immersive-stage backdrop behind multi-product showcases (carousel/comparison/bundle stages). May be a gradient or solid. Overrides --color-product-stage. Defaults to the sand gradient. */
  imageStage?: string;
  /** Thumbnail-strip tray behind product-view thumbnails. Overrides --color-product-strip. Defaults to the product-well surface. */
  thumbnailStrip?: string;
  /** Review-star fill color. Overrides --color-star. Defaults to the warm-amber design token. */
  starFill?: string;
  /**
   * On-stage nav-control scheme (carousel arrows, dots, secondary button). Not a color — selects a bundle
   * of control tokens. Default (unset) = frosted-white controls (for warm/dark stages); 'dark' = dark
   * accent-inked glass + neutral button, for brands with a light imageStage (e.g. Ghurka).
   */
  stageControls?: 'dark';
}

export interface ClientBrandFonts {
  brandFont: string;
  displayFont?: string;
  bodyFont?: string;
  brandFontSize: string;
  brandFontWeight: number;
  brandFontStyle: 'normal' | 'italic';
}

export interface ClientBrandConfig {
  brandName: string;
  displayName: string;
  iconText: string;
  tagline: string;
  defaultPlaceholder: string;
  siteNavButtonText: string;
  headerTitle?: string;
  welcomeMessage?: string;
  pdpWelcomeMessage?: string;
  /** Hide the brand logomark inside the conversation (message avatars, panel/inline-PDP headers). Entry-point launchers keep it. */
  hideChatLogomark?: boolean;
  colors: ClientBrandColors;
  fonts: ClientBrandFonts;
  iconSvgPaths: string[];
  iconViewBox: string;
  iconFillRule?: 'nonzero' | 'evenodd';
  suggestions: string[];
  homeChips: string[];
  featuredChips?: string[];
  collectionSuggestions: Record<string, string[]>;
  cartChips?: string[];
  cartSuggestions?: string[];
  currency: string;
  locale: string;
  modelBaseImages?: Record<string, string[]>;
  features: {
    showSizeHelp: boolean;
    showBodyShapeSelector: boolean;
    showSizeChart: boolean;
    showSizeRecommendation: boolean;
    photoAction: 'virtualTryOn' | 'skinAnalysis' | 'arPreview' | 'none';
  };
  labels: {
    colorTerm: string;
    materialTerm: string;
    sizeTerm: string;
    bundleTerm: string;
  };
  personalizedPDP?: {
    enabled?: boolean;
    cardTitle?: string;
  };
  inlinePDP?: {
    enabled?: boolean;
  };
  /**
   * Product-image normalization for transparent-cutout catalogs. Omit to default to 'cropToTallest'
   * wherever every item in a group has a transparent image + bounding box. (Synced with the server
   * ClientBrandConfig in config/types.ts.)
   */
  imageNormalization?: {
    mode: 'cropToTallest' | 'normalize' | 'off';
    fillTallest?: number;
    baseline?: number;
  };
  /**
   * Customer-service contact info for the handoff card (displayContactSupport).
   * (Synced with the server ClientBrandConfig in config/types.ts.)
   */
  support?: {
    email?: string;
    contactUrl?: string;
  };
}

const BrandConfigContext = createContext<ClientBrandConfig | null>(null);

export function useBrandConfig(): ClientBrandConfig {
  const ctx = useContext(BrandConfigContext);
  if (!ctx) {
    throw new Error('useBrandConfig must be used within a BrandConfigProvider');
  }
  return ctx;
}

export function BrandConfigProvider({
  config,
  children,
}: {
  config: ClientBrandConfig;
  children: React.ReactNode;
}) {
  return (
    <BrandConfigContext.Provider value={config}>
      {children}
    </BrandConfigContext.Provider>
  );
}
